import React, { useEffect, useState } from "react";
import { Box, MenuItem, Select, TextField, CircularProgress } from "@mui/material";
import Modal, { ModalHead, ModalBody, ModalFoot } from "../../../../../component/Modal";
import BtnPrimary from "../../../../../component/PrimaryButton";
import BtnSecondary from "../../../../../component/SecondaryButton";
import ErrorMsg from "../../../../../component/ErrorMsg";
import { useNotify } from "../../../../../component/NotificationProvider";
import { getLegalUnitList } from "../../../../../services/globalData";
import { transferAsset } from "../../../../../services/asset";
import AssetTransferHistory from "./AssetTransferHistory";
import { errorMessageFrom } from "../../../../../utils/formErrors";
import { selectSx } from "../../../../../component/listing";
import { tokens } from "../../../../../theme";


/**
 * Moving an asset from one legal entity to another.
 *
 * A reason is required, and the field is not optional politeness: the record of
 * WHY a machine left one company's books is the part somebody needs months
 * later, and it cannot be reconstructed afterwards. The server refuses the move
 * without one, so this asks for it rather than letting a save fail.
 *
 * The history is shown in the same place as the action. Somebody about to move
 * an asset for the third time should be able to see the previous two without
 * going looking, and it is the clearest way to say that the move is recorded.
 */
const TransferAssetDialog = ({ asset, open, onClose, onDone }) => {
  const notify = useNotify();
  const entities = getLegalUnitList() || [];
  const [to, setTo] = useState("");
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const currentId = String(asset?.legalUnit?._id || asset?.legalUnit || "");
  const currentName =
    entities.find((e) => String(e._id) === currentId)?.name || "no entity yet";

  useEffect(() => {
    if (!open || !asset?._id) return;
    setTo("");
    setReason("");
    setNote("");
    setErrors({});
  }, [open, asset?._id]);

  const save = async () => {
    const e = {};
    if (!to) e.to = "Choose the entity this is moving to";
    if (!reason.trim()) e.reason = "Say why it is moving — this is what the record keeps";
    setErrors(e);
    if (Object.keys(e).length) return;

    setSaving(true);
    try {
      const res = await transferAsset(asset._id, { legalUnit: to, reason: reason.trim(), note: note.trim() || undefined });
      if (res?.success === false) {
        notify.error(errorMessageFrom(res, "Could not move the asset"));
        return;
      }
      notify.success(`${asset.name} moved to ${entities.find((x) => String(x._id) === String(to))?.name || "the new entity"}`);
      onDone?.();
      onClose?.();
    } catch (err) {
      notify.error(err?.message || "Could not move the asset");
    } finally {
      setSaving(false);
    }
  };

  if (!asset) return null;

  return (
    <Modal open={open} onClose={onClose} width="34rem">
      <ModalHead
        title="Move to another legal entity"
        subtitle={`${asset.name} — currently owned by ${currentName}`}
        onClose={onClose}
      />
      <ModalBody>
        <Box sx={{ fontSize: "0.8125rem", fontWeight: 600, color: tokens.text, mb: "0.5rem" }}>
          Moving to *
        </Box>
        <Select
          fullWidth size="small" displayEmpty value={to}
          onChange={(ev) => { setTo(ev.target.value); setErrors((p) => ({ ...p, to: "" })); }}
          error={Boolean(errors.to)} sx={{ ...selectSx, mb: errors.to ? 0 : "1rem" }}
        >
          <MenuItem value="" disabled>Choose a legal entity</MenuItem>
          {entities
            // The entity it already belongs to is not a move.
            .filter((e) => String(e._id) !== currentId)
            .map((e) => <MenuItem key={e._id} value={e._id}>{e.name}</MenuItem>)}
        </Select>
        <ErrorMsg>{errors.to}</ErrorMsg>
        {errors.to ? <Box sx={{ mb: "1rem" }} /> : null}

        <Box sx={{ fontSize: "0.8125rem", fontWeight: 600, color: tokens.text, mb: "0.5rem" }}>
          Why is it moving? *
        </Box>
        <TextField
          fullWidth size="small" value={reason}
          placeholder="e.g. Reassigned to the consulting arm after the team transfer"
          onChange={(ev) => { setReason(ev.target.value); setErrors((p) => ({ ...p, reason: "" })); }}
          error={Boolean(errors.reason)}
          sx={{ mb: errors.reason ? 0 : "1rem" }}
        />
        <ErrorMsg>{errors.reason}</ErrorMsg>
        {errors.reason ? <Box sx={{ mb: "1rem" }} /> : null}

        <Box sx={{ fontSize: "0.8125rem", fontWeight: 600, color: tokens.text, mb: "0.5rem" }}>
          Note (optional)
        </Box>
        <TextField
          fullWidth size="small" multiline minRows={2} value={note}
          placeholder="Anything a future reader would want alongside the reason"
          onChange={(ev) => setNote(ev.target.value)}
          sx={{ mb: "1.5rem" }}
        />

        <Box sx={{ fontSize: "0.8125rem", fontWeight: 600, color: tokens.text, mb: "0.5rem" }}>
          Previous moves
        </Box>
        <AssetTransferHistory assetId={asset._id} dense />
      </ModalBody>
      <ModalFoot note="Recorded against the asset, with who moved it and when.">
        <BtnSecondary onClick={onClose} sx={{ borderRadius: "8px" }}>Cancel</BtnSecondary>
        <BtnPrimary onClick={save} disabled={saving} sx={{ borderRadius: "8px" }}>
          {saving ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Move asset"}
        </BtnPrimary>
      </ModalFoot>
    </Modal>
  );
};

export default TransferAssetDialog;
